import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import '../styles/Login.css';

const TAG_TYPE_ICONS = {
  vocabulary: '📖',
  grammar: '✏️',
  vocabulary_topic: '🏷️',
};

const TagDetailPopup = ({ tag, tagType, onClose }) => {
  const content = tag.tag_content || {};
  const nameVi = content.name?.vi;
  const nameEn = content.name?.en || tag.tag_name;
  const definition = content.definition?.vi || content.definition?.en;
  const usage = content.usage?.vi || content.usage?.en;
  const example = content.example?.vi || content.example?.en;
  const description = content.description?.vi || content.description?.en;
  const level = content.level;
  const formula = content.formula;
  const pos = content.pos;

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div
        className="bg-white rounded-2xl shadow-2xl max-w-md w-full max-h-[80vh] overflow-y-auto p-6 relative"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close */}
        <button onClick={onClose} className="absolute top-3 right-3 text-gray-400 hover:text-gray-600">
          <span className="material-symbols-outlined">close</span>
        </button>

        {/* Header */}
        <div className="flex items-center gap-3 mb-4">
          <span className="text-3xl">{TAG_TYPE_ICONS[tagType] || '📋'}</span>
          <div>
            <h3 className="font-headline font-bold text-lg text-hp-on-surface">{nameEn}</h3>
            {nameVi && nameVi !== nameEn && (
              <p className="text-sm text-hp-on-surface-variant">{nameVi}</p>
            )}
          </div>
        </div>

        {/* Score badge */}
        <div className="mb-4">
          <div className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm font-bold ${
            tag.mastery_score >= 70 ? 'bg-green-100 text-green-700'
              : tag.mastery_score >= 40 ? 'bg-blue-100 text-blue-700'
              : 'bg-orange-100 text-orange-700'
          }`}>
            {tag.mastery_score >= 70 ? '🌟' : tag.mastery_score >= 40 ? '📈' : '🌱'}
            {tag.mastery_score}%
          </div>
        </div>

        {/* Details */}
        <div className="space-y-3 text-sm">
          {level && (
            <div className="flex items-start gap-2">
              <span className="text-gray-400 mt-0.5">🎯</span>
              <div><span className="font-semibold text-gray-600">Cấp độ:</span> {level}</div>
            </div>
          )}
          {pos && (
            <div className="flex items-start gap-2">
              <span className="text-gray-400 mt-0.5">🔤</span>
              <div><span className="font-semibold text-gray-600">Loại từ:</span> {pos}</div>
            </div>
          )}
          {definition && (
            <div className="flex items-start gap-2">
              <span className="text-gray-400 mt-0.5">📝</span>
              <div>
                <span className="font-semibold text-gray-600">Định nghĩa:</span>
                <p className="text-gray-700 mt-0.5">{definition}</p>
              </div>
            </div>
          )}
          {description && !definition && (
            <div className="flex items-start gap-2">
              <span className="text-gray-400 mt-0.5">📝</span>
              <div>
                <span className="font-semibold text-gray-600">Mô tả:</span>
                <p className="text-gray-700 mt-0.5">{description}</p>
              </div>
            </div>
          )}
          {formula && (
            <div className="flex items-start gap-2">
              <span className="text-gray-400 mt-0.5">🧮</span>
              <div>
                <span className="font-semibold text-gray-600">Công thức:</span>
                <p className="text-gray-700 mt-0.5 font-mono bg-gray-50 px-2 py-1 rounded">{formula}</p>
              </div>
            </div>
          )}
          {usage && (
            <div className="flex items-start gap-2">
              <span className="text-gray-400 mt-0.5">💡</span>
              <div>
                <span className="font-semibold text-gray-600">Cách dùng:</span>
                <p className="text-gray-700 mt-0.5">{usage}</p>
              </div>
            </div>
          )}
          {example && (
            <div className="flex items-start gap-2">
              <span className="text-gray-400 mt-0.5">💬</span>
              <div>
                <span className="font-semibold text-gray-600">Ví dụ:</span>
                <p className="text-gray-700 mt-0.5" dangerouslySetInnerHTML={{ __html: example }} />
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const PlacementResult = () => {
  const navigate = useNavigate();
  const [result, setResult] = useState(null);
  const [selectedTag, setSelectedTag] = useState(null);
  const [selectedTagType, setSelectedTagType] = useState(null);

  useEffect(() => {
    const raw = localStorage.getItem('placementResult');
    if (raw) {
      try {
        setResult(JSON.parse(raw));
      } catch {
        navigate('/dashboard');
      }
    } else {
      navigate('/dashboard');
    }
  }, [navigate]);

  if (!result) return null;

  const { total_correct, total_questions, strengths = [], weaknesses = [], all_tested_tags = [] } = result;
  const strengthList = strengths || [];
  const weaknessList = weaknesses || [];

  // all_tested_tags is already grouped: [{tag_type_code, tag_type_label, tags: [{tag_id, tag_name, tag_code, tag_content, mastery_score}]}]
  const tagsByType = (all_tested_tags || []).reduce((acc, group) => {
    const label = group.tag_type_label || group.tag_type_code || 'Khác';
    acc[label] = group.tags || [];
    return acc;
  }, {});

  const percent = Math.round((total_correct / total_questions) * 100);

  let levelLabel, levelColor, levelEmoji;
  if (percent >= 80) {
    levelLabel = 'Xuất sắc';
    levelColor = 'text-green-600';
    levelEmoji = '🏆';
  } else if (percent >= 50) {
    levelLabel = 'Khá tốt';
    levelColor = 'text-blue-600';
    levelEmoji = '🌟';
  } else {
    levelLabel = 'Đang phát triển';
    levelColor = 'text-orange-500';
    levelEmoji = '🌱';
  }

  const handleGoToDashboard = () => {
    localStorage.setItem('hasCompletedPlacement', 'true');
    navigate('/dashboard');
  };

  return (
    <div className="ob-sky-voyager font-body min-h-screen flex flex-col items-center justify-center p-4 relative overflow-x-hidden">
      <div className="fixed inset-0 ob-cloud-pattern pointer-events-none" />
      <div className="fixed inset-0 ob-star-pattern opacity-30 pointer-events-none" />

      <main className="w-full max-w-[640px] my-8 z-10">
        {/* Hero */}
        <div className="bg-white rounded-2xl shadow-[0px_12px_32px_rgba(94,46,26,0.08)] p-8 mb-6 text-center">
          <div className="text-6xl mb-4">{levelEmoji}</div>
          <h1 className="font-headline text-3xl font-extrabold text-hp-on-surface mb-2">
            Kết quả đánh giá
          </h1>
          <p className={`text-xl font-bold ${levelColor} mb-6`}>{levelLabel}</p>

          {/* Score ring */}
          <div className="flex items-center justify-center mb-6">
            <div className="relative w-32 h-32">
              <svg className="w-full h-full -rotate-90" viewBox="0 0 120 120">
                <circle cx="60" cy="60" r="50" fill="none" stroke="#e5e7eb" strokeWidth="10" />
                <circle
                  cx="60" cy="60" r="50"
                  fill="none"
                  stroke="url(#scoreGradient)"
                  strokeWidth="10"
                  strokeLinecap="round"
                  strokeDasharray={`${2 * Math.PI * 50}`}
                  strokeDashoffset={`${2 * Math.PI * 50 * (1 - percent / 100)}`}
                  className="transition-all duration-1000"
                />
                <defs>
                  <linearGradient id="scoreGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                    <stop offset="0%" stopColor="#5c8d3e" />
                    <stop offset="100%" stopColor="#84c04b" />
                  </linearGradient>
                </defs>
              </svg>
              <div className="absolute inset-0 flex flex-col items-center justify-center">
                <span className="text-2xl font-extrabold text-hp-on-surface">{total_correct}/{total_questions}</span>
                <span className="text-xs text-hp-on-surface-variant font-semibold">{percent}%</span>
              </div>
            </div>
          </div>

          <p className="text-hp-on-surface-variant text-sm">
            Con trả lời đúng <strong className="text-hp-on-surface">{total_correct}</strong> trong số{' '}
            <strong className="text-hp-on-surface">{total_questions}</strong> câu hỏi
          </p>
        </div>

        {/* Strengths & Weaknesses */}
        <div className="grid grid-cols-1 gap-4 mb-6">
          {strengthList.length > 0 && (
            <div className="bg-white rounded-2xl shadow-[0px_12px_32px_rgba(94,46,26,0.08)] p-6">
              <h2 className="font-headline font-bold text-lg text-hp-on-surface mb-4 flex items-center gap-2">
                <span className="text-2xl">💪</span>
                Điểm mạnh của con
              </h2>
              <div className="flex flex-wrap gap-2">
                {strengthList.map((tag, i) => (
                  <span
                    key={i}
                    className="px-3 py-1 bg-green-50 text-green-700 border border-green-200 rounded-full text-sm font-semibold"
                  >
                    {tag.tag_name || tag}
                  </span>
                ))}
              </div>
            </div>
          )}

          {weaknessList.length > 0 && (
            <div className="bg-white rounded-2xl shadow-[0px_12px_32px_rgba(94,46,26,0.08)] p-6">
              <h2 className="font-headline font-bold text-lg text-hp-on-surface mb-4 flex items-center gap-2">
                <span className="text-2xl">📚</span>
                Cần cải thiện
              </h2>
              <div className="flex flex-wrap gap-2">
                {weaknessList.map((tag, i) => (
                  <span
                    key={i}
                    className="px-3 py-1 bg-orange-50 text-orange-700 border border-orange-200 rounded-full text-sm font-semibold"
                  >
                    {tag.tag_name || tag}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* All tested tags grouped by type */}
        {Object.keys(tagsByType).length > 0 && (
          <div className="bg-white rounded-2xl shadow-[0px_12px_32px_rgba(94,46,26,0.08)] p-6 mb-6">
            <h2 className="font-headline font-bold text-lg text-hp-on-surface mb-4 flex items-center gap-2">
              <span className="text-2xl">📋</span>
              Các kỹ năng đã được đánh giá
            </h2>
            <div className="space-y-4">
              {Object.entries(tagsByType).map(([groupName, tags]) => {
                const typeCode = (all_tested_tags.find((g) => (g.tag_type_label || g.tag_type_code) === groupName) || {}).tag_type_code || '';
                return (
                  <div key={groupName}>
                    <p className="text-xs font-bold text-hp-on-surface-variant uppercase tracking-wider mb-2 flex items-center gap-1">
                      <span>{TAG_TYPE_ICONS[typeCode] || '📋'}</span>
                      {groupName}
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {tags.map((tag) => {
                        const score = tag.mastery_score ?? 0;
                        let colorClass = 'bg-orange-50 text-orange-700 border-orange-200 hover:bg-orange-100';
                        if (score >= 70) colorClass = 'bg-green-50 text-green-700 border-green-200 hover:bg-green-100';
                        else if (score >= 40) colorClass = 'bg-blue-50 text-blue-700 border-blue-200 hover:bg-blue-100';
                        return (
                          <button
                            key={tag.tag_id}
                            onClick={() => { setSelectedTag(tag); setSelectedTagType(typeCode); }}
                            className={`px-3 py-1.5 border rounded-full text-sm font-semibold flex items-center gap-1.5 cursor-pointer transition-all duration-200 ${colorClass}`}
                          >
                            <span className="text-base">{TAG_TYPE_ICONS[typeCode] || '📋'}</span>
                            {tag.tag_name}
                            <span className="text-xs opacity-70">{score}%</span>
                            <span className="material-symbols-outlined text-sm opacity-50">info</span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="mt-4 flex gap-4 text-xs text-hp-on-surface-variant">
              <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-full bg-green-400 inline-block" /> Tốt (≥70%)</span>
              <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-full bg-blue-400 inline-block" /> Khá (≥40%)</span>
              <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-full bg-orange-400 inline-block" /> Cần luyện</span>
            </div>
          </div>
        )}

        {/* Info box */}
        <div className="bg-blue-50 border-l-4 border-blue-400 p-4 mb-6 rounded-xl">
          <div className="flex gap-3">
            <span className="text-2xl">🤖</span>
            <p className="text-gray-700 text-sm">
              HuPath đã tạo lộ trình học cá nhân hóa dựa trên kết quả này.
              Hãy bắt đầu hành trình của con nhé!
            </p>
          </div>
        </div>

        {/* CTA */}
        <button
          onClick={handleGoToDashboard}
          className="w-full hp-button-gradient text-white font-headline font-bold text-xl py-4 rounded-xl shadow-lg hover:scale-[1.02] active:scale-[0.98] transition-all duration-300 flex items-center justify-center gap-2"
        >
          Vào trang học của con
          <span className="material-symbols-outlined">arrow_forward</span>
        </button>
      </main>

      {/* Tag detail popup */}
      {selectedTag && (
        <TagDetailPopup
          tag={selectedTag}
          tagType={selectedTagType}
          onClose={() => { setSelectedTag(null); setSelectedTagType(null); }}
        />
      )}

      <div className="fixed bottom-[-100px] left-1/2 -translate-x-1/2 w-[120%] h-[300px] bg-white rounded-[50%] blur-3xl opacity-60 z-0" />
    </div>
  );
};

export default PlacementResult;
